import React from 'react';
import { Box, Divider, Popover, Typography } from '@mui/material';
import { StatPopoverState } from '../../hooks/useStatPopover';
import { StatModifier } from '../../utils/splitStats';

interface StatBreakdownPopoverProps {
  popover: StatPopoverState | null;
  onClose: () => void;
}

const formatModifierValue = (modifier: StatModifier): string => {
  if (modifier.type === 'set') {
    return `= ${modifier.value}`;
  }
  return modifier.value >= 0 ? `+${modifier.value}` : `${modifier.value}`;
};

export const StatBreakdownPopover: React.FC<StatBreakdownPopoverProps> = ({ popover, onClose }) => (
  <Popover
    open={Boolean(popover)}
    anchorEl={popover?.anchor}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
  >
    <Box sx={{ p: 2, minWidth: 180 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {popover?.label}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
        <Typography variant="body2" color="textSecondary">Base</Typography>
        <Typography variant="body2">{popover?.base}</Typography>
      </Box>

      {(popover?.modifiers.length ?? 0) > 0 ? (
        <>
          <Divider sx={{ my: 1 }} />
          {popover!.modifiers.map((modifier, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
              <Typography variant="body2" color="textSecondary">{modifier.label}</Typography>
              <Typography
                variant="body2"
                sx={{ color: modifier.value < 0 ? 'error.main' : modifier.value > 0 ? 'success.main' : 'inherit' }}
              >
                {formatModifierValue(modifier)}
              </Typography>
            </Box>
          ))}
        </>
      ) : (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
          No modifiers active
        </Typography>
      )}

      {popover?.adjusted !== popover?.base && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{popover?.adjusted}</Typography>
          </Box>
        </>
      )}
    </Box>
  </Popover>
);
